// Spaces signal — list of spaces visible to the current user, sorted by
// sort_order. Loaded once at app startup alongside the project list.
// Mirrors the pattern of store/projects.ts.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { Space, SpaceCreate, SpaceUpdate } from "@juuplan/shared";
import {
  listSpaces,
  createSpace as apiCreateSpace,
  updateSpace as apiUpdateSpace,
  deleteSpace as apiDeleteSpace,
} from "../lib/api";

export interface SpacesState {
  loading: boolean;
  items: Space[];
  error: string | null;
}

const initial: SpacesState = { loading: true, items: [], error: null };
const spacesSignal: Signal<SpacesState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadSpaces(): Promise<void> {
  spacesSignal.value = { ...spacesSignal.value, loading: true, error: null };
  try {
    const raw = await listSpaces();
    const items = [...raw].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    spacesSignal.value = { loading: false, items, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    spacesSignal.value = { loading: false, items: [], error: message };
  } finally {
    inFlight = null;
  }
}

/** Subscribe to the spaces list. Triggers the initial load on first call. */
export function useSpaces(): SpacesState {
  useEffect(() => {
    if (!inFlight && spacesSignal.value.loading) {
      inFlight = loadSpaces();
    }
  }, []);
  return spacesSignal.value;
}

/** Force a fresh fetch — use after any space mutation. */
export function refreshSpaces(): Promise<void> {
  inFlight = loadSpaces();
  return inFlight;
}

/** Create a space and refresh the cache. */
export async function createSpace(body: SpaceCreate): Promise<Space> {
  const created = await apiCreateSpace(body);
  await refreshSpaces();
  return created;
}

/** Update a space and refresh the cache. */
export async function updateSpace(id: number, body: SpaceUpdate): Promise<Space> {
  const updated = await apiUpdateSpace(id, body);
  // Optimistically update the signal so UI reacts immediately.
  spacesSignal.value = {
    ...spacesSignal.value,
    items: spacesSignal.value.items.map((s) => (s.id === id ? updated : s)),
  };
  return updated;
}

/** Delete a space and refresh the cache. */
export async function deleteSpace(id: number): Promise<void> {
  await apiDeleteSpace(id);
  spacesSignal.value = {
    ...spacesSignal.value,
    items: spacesSignal.value.items.filter((s) => s.id !== id),
  };
}
